import { createFileRoute } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { useTrack } from "../lib/analytics";
import { canonical, documentHeaders } from "../lib/seo";

/**
 * Deep link into the installed app's deletion screen.
 *
 * ONE scheme for every environment — dev, preview and production all register
 * `bibleshield://` (apps/mobile/app.config.ts: `getDynamicAppConfig` returns the
 * same IDENTITY for all three, because the Family Controls entitlement Apple
 * grants is per-App-ID and splitting identities would mean re-requesting it).
 * So this link works against whichever build is installed, and there is no
 * `-dev` variant to keep in step.
 *
 * The app answers it in `lib/deleteAccountLink.ts` and lands on a ROOT-level
 * screen, which is what lets a subscriber whose plan expired reach deletion
 * without the paywall coming down.
 */
const APP_DEEP_LINK = "bibleshield://delete-account";

export const Route = createFileRoute("/delete")({
  headers: documentHeaders,
  head: () => ({
    meta: [{ title: "Delete your account — Bible Shield" }],
    links: [{ rel: "canonical", href: canonical("/delete") }],
  }),
  component: DeleteAccount,
});

function DeleteAccount() {
  const track = useTrack();

  return (
    <PageChrome title="Delete your account">
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        You can permanently delete your Bible Shield account and all of its data
        at any time, right from the app. If Bible Shield is installed, the
        button below takes you straight there.
      </p>

      <div className="mt-8 flex flex-col items-start gap-3">
        <a
          href={APP_DEEP_LINK}
          // The scheme only resolves if the app is installed, so a click here is
          // a signal in its own right: it separates people who already have
          // Bible Shield and want out from people who found this page by search.
          onClick={() => track("web_app_open_click", { from: "delete" })}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          open bible shield
        </a>
        <span className="text-[13px] text-ink2">
          Opens the app to Profile → Delete account.
        </span>
      </div>

      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          What gets deleted
        </h2>
        <ul className="mt-3 max-w-2xl list-disc space-y-1.5 pl-5 text-[16px] leading-relaxed text-ink2">
          <li>Your device profile and session</li>
          <li>All data associated with your device</li>
        </ul>
      </section>

      <div className="brutal mt-8 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
        Deletion is immediate and permanent — it can't be undone.
      </div>

      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          Prefer to do it manually?
        </h2>
        <ol className="mt-3 max-w-2xl list-decimal space-y-1.5 pl-5 text-[16px] leading-relaxed text-ink2">
          <li>Open the Bible Shield app.</li>
          <li>Go to the Profile tab.</li>
          <li>Tap “Delete account” and confirm.</li>
        </ol>
      </section>
    </PageChrome>
  );
}
